import React from 'react';
import desktopScreenshot from '../../img/lhscreenshot.png';
import heroBg from '../../img/lhcover.png'

const styles = {
  page: {
    backgroundColor: '#0f172a', // Deep slate/terminal background
    color: '#e2e8f0', 
    fontFamily: '"Inter", sans-serif',
    minHeight: '100vh',
  },
  // DARK HERO SECTION
  heroSection: {
    width: '100%',
    height: '60vh',
    backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 1)), url(${heroBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 20px',
    borderBottom: '4px solid #38bdf8', // Cyber/OS Cyan accent
  },
  heroTitle: {
    fontSize: '4.5rem',
    fontWeight: '900',
    marginBottom: '10px',
    color: '#f8fafc',
    letterSpacing: '-2px',
  },
  heroSubtitle: {
    fontSize: '1.4rem',
    fontWeight: '400',
    color: '#94a3b8',
    maxWidth: '600px',
    marginBottom: '30px',
  },
  // THE NEW LABELING SYSTEM
  badgeContainer: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  badgePrimary: {
    backgroundColor: '#38bdf8',
    color: '#0f172a',
    padding: '8px 16px',
    borderRadius: '4px',
    fontFamily: '"Courier New", monospace', // Monospace to fit the OS vibe
    fontWeight: 'bold',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
  },
  badgeSecondary: {
    backgroundColor: '#1e293b',
    color: '#10b981', // Emerald green for "Clean/AI-Free"
    border: '1px solid #10b981',
    padding: '8px 16px',
    borderRadius: '4px',
    fontFamily: '"Courier New", monospace',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
  },
  // MAIN CONTENT
  content: {
    maxWidth: '1000px',
    margin: '-40px auto 0', 
    padding: '50px',
    backgroundColor: '#1e293b', // Dark surface
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)', 
    position: 'relative',
    zIndex: 10,
    border: '1px solid #334155',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#f8fafc',
    textAlign: 'center',
  },
  paragraph: {
    fontSize: '1.1rem',
    lineHeight: '1.8',
    color: '#cbd5e1',
    marginBottom: '20px',
    textAlign: 'center',
  },
  screenshotContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '40px 0',
  },
  screenshot: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '4px', // Sharper corners for pixel art feel
    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
    border: '2px solid #334155',
  },
  // GRID
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '30px',
    marginTop: '40px',
  },
  featureCard: {
    padding: '30px',
    backgroundColor: '#0f172a',
    borderRadius: '8px',
    borderLeft: '4px solid #38bdf8', 
  },
  featureTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#f8fafc',
    fontFamily: '"Courier New", monospace', // OS font for headers
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginTop: '50px',
  },
  primaryButton: {
    padding: '16px 40px',
    backgroundColor: '#38bdf8', 
    color: '#0f172a',
    fontWeight: 'bold',
    border: 'none',
    fontSize: '1.1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background 0.2s',
  },
  secondaryButton: {
    padding: '16px 40px',
    backgroundColor: 'transparent',
    color: '#38bdf8',
    fontWeight: 'bold',
    border: '2px solid #38bdf8',
    fontSize: '1.1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
  }
};

export default function LonelyHandsPage() {
  return (
    <div style={styles.page}>
      
      {/* HERO SECTION */}
      <div style={styles.heroSection}>
        <h1 style={styles.heroTitle}>LonelyHands</h1>
        <p style={styles.heroSubtitle}>
          A pixel-art desktop simulator about the business of digital intimacy.
        </p>
        
        {/* THE BADGE SYSTEM */}
        <div style={styles.badgeContainer}>
          <span style={styles.badgePrimary}>Status: Prototyping</span>
          <span style={styles.badgeSecondary}>100% Human-Made (No AI)</span>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={styles.content}>
        
        <h2 style={styles.sectionTitle}>Totally Not an OF Simulator</h2>
        <p style={styles.paragraph}>
          You are a struggling college student drowning in tuition debt. Desperate for cash, you stumble into a highly lucrative, morally grey gig: working as a "ghostwriter" for top models on a massive subscription-based adult platform.
        </p>
        <p style={styles.paragraph}>
          The clients think they are talking to their favorite creators. In reality, they are talking to you. 
          Your job is to manage the chat queues, string them along, and maximize tips without crossing your own personal lines.
        </p>

        {/* DESKTOP UI SHOWCASE */}
        <div style={styles.screenshotContainer}>
          <img src={desktopScreenshot} alt="Simulated Desktop UI" style={styles.screenshot} />
        </div>

        {/* FEATURES GRID */}
        <div style={styles.grid}>
          
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>&gt; DESKTOP_OS</h3>
            <p style={{color: '#94a3b8', lineHeight: '1.6'}}>
              The core loop takes place on a simulated computer desktop rendered in minimalist pixel art. Juggle multiple clients as you build the perfect response for each of them, balancing sweetness, teasing, desperation, etc... for maximum tips!
            </p>
          </div>

          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>&gt; MORAL_GREY</h3>
            <p style={{color: '#94a3b8', lineHeight: '1.6'}}>
              Explore the dark side of parasocial relationships. When your own survival is on the line, how far will you manipulate a stranger on the other side of the screen? With rent coming up, an your roommate out of a job, its up to you to keep you both afloat.
            </p>
          </div>

          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>&gt; MANAGE_MODELS</h3>
            <p style={{color: '#94a3b8', lineHeight: '1.6'}}>
              Manage up to 4 different model's chats at the same time! Breaking character or sending the wrong message can be disastrous, so stay focused as your salary depends on it.
            </p>
          </div>

        </div>

        {/* TEASER BUTTONS */}
        <div style={styles.buttonGroup}>
          <a 
            href="https://x.com/ForeplayGames" 
            target="_blank"
            rel="noopener noreferrer"
            style={styles.primaryButton}
          >
            Follow on Twitter
          </a>
          <a 
            href="/devlog" 
            style={styles.secondaryButton}
          >
            Read the Devlogs
          </a>
        </div>

      </div>
    </div>
  );
}